import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { getBlogPosts, getSite } from '../../services/CommonServices';
import GlobalHeader from '../GlobalHeader';
import GlobalFooter from '../GlobalFooter';
import { ShadowContainer, NoShadowContainer } from '../Layout';
import { useInstance } from '../../contexts/InstanceContext';

// Outer wrapper with gray background - uses calc to account for Header navbar (40px)
const PageWrapper = styled.div`
  background-color: #f9f9f9;
  min-height: calc(100dvh - 40px);
  display: flex;
  flex-direction: column;
`;

// Simple wrapper - layout handled by ShadowContainer/NoShadowContainer
const SiteWrapper = styled.div`
  transition: all 0.3s;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

// Main content area that grows to push footer down
const MainContent = styled.main`
  flex: 1;
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 40px 20px 60px;

  @media (max-width: 768px) {
    padding: 30px 15px 40px;
  }
`;

const PageTitle = styled.h1`
  text-align: center;
  margin-bottom: 40px;
  font-size: 2.2rem;
  font-weight: 700;
  color: #333;

  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 30px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
  }
`;

const ThumbnailLink = styled(Link)`
  display: block;
  overflow: hidden;
`;

const Thumbnail = styled.div`
  height: 200px;
  background-image: url(${props => props.url});
  background-size: cover;
  background-position: center;
  background-color: #f0f0f0;
  transition: transform 0.3s ease;

  ${Card}:hover & {
    transform: scale(1.05);
  }

  @media (max-width: 640px) {
    height: 180px;
  }
`;

const Content = styled.div`
  padding: 20px;

  @media (max-width: 640px) {
    padding: 16px;
  }
`;

const DateMeta = styled.div`
  font-size: 0.85rem;
  color: #888;
  margin-bottom: 8px;
  font-weight: 400;
`;

const Title = styled.h3`
  margin: 0 0 12px;
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.5;
  color: #333;

  a {
    color: inherit;
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: ${props => props.theme.colors?.primaryColor || '#c41e3a'};
    }
  }

  @media (max-width: 640px) {
    font-size: 1rem;
  }
`;

const Excerpt = styled.p`
  color: #666;
  font-size: 0.9rem;
  line-height: 1.7;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 640px) {
    font-size: 0.85rem;
    -webkit-line-clamp: 2;
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #666;
  font-size: 1rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #888;
  font-size: 1rem;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 40px;
  padding-top: 30px;
`;

const PageButton = styled.button`
  background-color: ${props => props.active ? (props.theme.colors?.primaryColor || '#c41e3a') : '#fff'};
  color: ${props => props.active ? '#fff' : '#666'};
  border: 1px solid ${props => props.active ? (props.theme.colors?.primaryColor || '#c41e3a') : '#ddd'};
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: ${props => props.active ? (props.theme.colors?.primaryColor || '#c41e3a') : '#f5f5f5'};
    border-color: ${props => props.theme.colors?.primaryColor || '#c41e3a'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.span`
  color: #666;
  font-size: 0.9rem;
`;

const HomeLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  margin-bottom: 16px;
  color: ${props => props.theme.colors?.primaryColor || '#c41e3a'};
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.1rem;
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

// Extract first image from HTML content
const extractFirstImage = (htmlContent) => {
  if (!htmlContent) return null;
  const imgMatch = htmlContent.match(/<img[^>]+src=["']([^"']+)["']/i);
  return imgMatch ? imgMatch[1] : null;
};

// Get thumbnail: prioritize post.thumbnail, then first image from content
const getPostThumbnail = (post) => {
  if (post.thumbnail) return post.thumbnail;
  const firstImage = extractFirstImage(post.content);
  return firstImage || null;
};

// Extract clean text from HTML content for excerpt
const getExcerpt = (htmlContent, maxLength = 150) => {
  if (!htmlContent) return '';

  // Create a temporary element to decode HTML entities
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;

  // Get text content (removes all HTML tags and decodes entities)
  let text = tempDiv.textContent || tempDiv.innerText || '';

  // Clean up whitespace (multiple spaces, newlines, tabs)
  text = text.replace(/\s+/g, ' ').trim();

  // Truncate and add ellipsis
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + '...';
  }
  return text;
};

const BlogList = (props) => {
  const { site, globalHeaderType, globalFooterType } = props;
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [siteId, setSiteId] = useState(null);
  const intl = useIntl();
  const { memberId } = useInstance();
  const POSTS_PER_PAGE = 12;

  const loadPosts = async (id, page = 1) => {
    try {
      setLoading(true);
      const postsRes = await getBlogPosts(id, { limit: POSTS_PER_PAGE, page, public: true });
      if (postsRes.data.success) {
        setPosts(postsRes.data.data.posts);
        setPagination({
          page: postsRes.data.data.pagination.page,
          totalPages: postsRes.data.data.pagination.totalPages,
          total: postsRes.data.data.pagination.total,
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!memberId) return;

    const fetchData = async () => {
      try {
        const siteRes = await getSite(memberId);
        if (siteRes.data.success) {
          const id = siteRes.data.data.id;
          setSiteId(id);
          await loadPosts(id, 1);
        }
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };
    fetchData();
  }, [memberId]);

  const handlePageChange = (newPage) => {
    if (siteId) {
      loadPosts(siteId, newPage);
      // Scroll to top of page
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Choose wrapper based on hasBackground setting
  const ContentWrapper = site?.hasBackground ? ShadowContainer : NoShadowContainer;

  if (loading) {
    return (
      <PageWrapper>
        <ContentWrapper hasShadow={site?.hasBackground}>
          <SiteWrapper className="App">
            <GlobalHeader site={site} headerType={globalHeaderType} />
            <MainContent>
              <Container>
                <LoadingContainer>
                  <FormattedMessage id="blog.loading" defaultMessage="Loading..." />
                </LoadingContainer>
              </Container>
            </MainContent>
            <GlobalFooter site={site} footerType={globalFooterType} />
          </SiteWrapper>
        </ContentWrapper>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <ContentWrapper hasShadow={site?.hasBackground}>
        <SiteWrapper className="App">
          <GlobalHeader site={site} headerType={globalHeaderType} />
          <MainContent>
            <Container>
              <HomeLink to="/">
                <FormattedMessage id="common.home" defaultMessage="Home" />
              </HomeLink>
              <PageTitle>
                <FormattedMessage id="blog.title" defaultMessage="Blog" />
              </PageTitle>
              {posts.length === 0 ? (
                <EmptyState>
                  <FormattedMessage id="blog.no_posts" defaultMessage="No posts available yet." />
                </EmptyState>
              ) : (
                <>
                  <Grid>
                    {posts.map(post => {
                      const thumbnailUrl = getPostThumbnail(post);
                      return (
                      <Card key={post.id}>
                        <ThumbnailLink to={`/blog/${post.slug}`}>
                          <Thumbnail url={thumbnailUrl || 'https://via.placeholder.com/400x250?text=No+Image'} />
                        </ThumbnailLink>
                        <Content>
                          <DateMeta>
                            {new Date(post.created_at).toLocaleDateString(intl.locale, {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </DateMeta>
                          <Title>
                            <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                          </Title>
                          <Excerpt>
                            {getExcerpt(post.content)}
                          </Excerpt>
                        </Content>
                      </Card>
                      );
                    })}
                  </Grid>
                  {pagination.totalPages > 1 && (
                    <PaginationWrapper>
                      <PageButton
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page <= 1}
                      >
                        <FormattedMessage id="common.prev" defaultMessage="Prev" />
                      </PageButton>
                      <PageInfo>
                        {pagination.page} / {pagination.totalPages}
                      </PageInfo>
                      <PageButton
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page >= pagination.totalPages}
                      >
                        <FormattedMessage id="common.next" defaultMessage="Next" />
                      </PageButton>
                    </PaginationWrapper>
                  )}
                </>
              )}
            </Container>
          </MainContent>
          <GlobalFooter site={site} footerType={globalFooterType} />
        </SiteWrapper>
      </ContentWrapper>
    </PageWrapper>
  );
};

export default BlogList;
